import React, { useState } from 'react';
import { PORTAL_VERSION } from '../../version';
import './ChangeLog.css';

// ─────────────────────────────────────────────────────────────────────────────────
// Data — add new entries to the TOP of this array on each release.
// Set current: true for the latest entry and false for all others.
// Dates must be Mon–Fri working days.
// ─────────────────────────────────────────────────────────────────────────────────

type ChangeType = 'feature' | 'fix' | 'perf' | 'security' | 'compliance' | 'removed';

interface Change {
  type: ChangeType;
  text: string;
}

interface VersionEntry {
  version: string;
  date: string;
  label: string;
  updatedBy?: string;
  current: boolean;
  changes: Change[];
}

const CHANGELOG: VersionEntry[] = [
  {
    version: '2.1.03',
    date: 'Monday 2 November 2026',
    label: 'Onboarder Historical Fleet & Client Admin Provisioning',
    updatedBy: 'Matthew Murphy',
    current: true,
    changes: [
      { type: 'feature', text: 'Onboarders can now add historic fleet records individually or through CSV onboarding uploads, including custom historical inception dates' },
      { type: 'compliance', text: 'Historic Onboarder vehicle additions are automatically recorded in the backdating audit trail with the onboarding reason and acting user' },
      { type: 'feature', text: 'Onboarders can now provision and manage Client Admin as well as standard Client accounts for operational client tenants, allowing a dedicated support email to have one uniquely named tenant-admin account per client' },
      { type: 'security', text: 'Client Admin provisioning remains API-enforced for non-Yourshield tenants only; Onboarders cannot create or manage staff, platform roles, or Yourshield accounts' },
    ],
  },
  {
    version: '2.1.02',
    date: 'Monday 26 October 2026',
    label: 'Onboarder Client User Management',
    updatedBy: 'Matthew Murphy',
    current: false,
    changes: [
      { type: 'feature', text: 'Onboarder colleagues can now access User Management to create, edit, invite, reset passwords for, activate/deactivate and reassign sites for standard client users across operational client accounts' },
      { type: 'security', text: 'Onboarder user management is enforced server-side: staff, privileged roles and the Yourshield platform-owner client are excluded from lists and cannot be accessed or managed through direct requests' },
      { type: 'fix', text: 'User Management now displays inactive client accounts so authorised Onboarders can reactivate them when required' },
    ],
  },
  {
    version: '2.1.01',
    date: 'Thursday 8 October 2026',
    label: 'Claims Experience, Evidence & Precise Incident Location',
    updatedBy: 'Matthew Murphy',
    current: false,
    changes: [
      { type: 'feature', text: 'Claims Dashboard now presents Open, Awaiting Information and Updates Due queues as clear icon-led cards with filtered claim lists' },
      { type: 'fix', text: 'Claims workflow validation is displayed inside the modal, modal close controls are restored, and wizard footer actions retain accessible spacing' },
      { type: 'feature', text: 'Claim intake now records structured incident location, postcode, selected map coordinates and an automatically retrieved What3Words reference through a protected server-side integration' },
      { type: 'fix', text: 'Incident postcode capture now accepts normal full UK postcodes without losing typing focus, waits until the postcode is complete, and then loads the marker map' },
      { type: 'feature', text: 'Claim evidence supports categorized staged uploads, collision sketch uploads and more guided required incident, driver, vehicle and third-party questions' },
      { type: 'feature', text: 'Client Cover Settings now supports a claims inbox and selection of multiple client users for Motor Claims notices' },
    ],
  },
  {
    version: '2.0.08',
    date: 'Thursday 8 October 2026',
    label: 'Client-Scoped Motor Claims & Claims Manager Workflow',
    updatedBy: 'Matthew Murphy',
    current: false,
    changes: [
      { type: 'feature',  text: 'Motor Claims can now be enabled individually in Client Cover Settings; disabled clients have no claims navigation, claim records or submission access' },
      { type: 'feature',  text: 'Enabled clients can register and track motor claims through a guided incident, driver, vehicle, third-party, witness, evidence and final-confirmation workflow' },
      { type: 'feature',  text: 'Claims dashboard added with open-claim status, fault view, latest and next-update dates, client claim timelines, private evidence uploads and completed claim-form PDF generation' },
      { type: 'feature',  text: 'New Claims Manager role supports triage, internal and client-visible notes, claim correspondence and evidence upload, status progression, next-update scheduling and operational claim queues' },
      { type: 'security', text: 'Claim evidence is stored in a private managed-identity Azure Blob container and can only be accessed through authorised client or claims-staff portal requests' },
      { type: 'feature',  text: 'Claim submission and client-notified updates send branded notifications with the completed motor claim form attached; due and overdue claim updates produce internal reminders' },
    ],
  },
  {
    version: '2.0.07',
    date: 'Monday 28 September 2026',
    label: 'Trailer Declared Value Entry Reliability',
    updatedBy: 'Matthew Murphy',
    current: false,
    changes: [
      { type: 'fix',     text: 'Trailer declared-value entry now preserves the amount while users type, preventing formatting characters from inflating the value; it formats as pounds and pence when the field loses focus' },
      { type: 'feature', text: 'Trailer declared-value confirmation now shows the entered amount in pounds and pence alongside an amount-in-words banner, matching the vehicle value experience' },
    ],
  },
  {
    version: '2.0.06',
    date: 'Monday 28 September 2026',
    label: 'Fleet Reconciliation, Trailer Register & Account Validation',
    updatedBy: 'Matthew Murphy',
    current: false,
    changes: [
      { type: 'feature',    text: 'Admin Insights now includes Fleet Reconciliation — opening fleet baselines, additions, removals and closing fleet counts, with date filtering and Excel export' },
      { type: 'fix',        text: 'Portal date and time displays now consistently use Europe/London formatting, preserving calendar dates and correctly presenting UK local times throughout vehicle and reporting views' },
      { type: 'feature',    text: 'Policy-period renewal processing now carries eligible vehicles forward with clear audit-history markers and portal-compatible archived snapshots' },
      { type: 'feature',    text: 'Client-configurable Trailer Register added under Vehicles — authorised users can add and remove trailers requiring motor fleet cover, with policy guidance, client-scoped access and a full audit trail' },
      { type: 'feature',    text: 'Trailer Cover is enabled per client in Cover Settings; trailer registration and chassis details are optional when not readily available, while make/model and declared value remain required' },
      { type: 'fix',        text: 'Trailer declared values now display as pounds and pence with an amount-in-words confirmation banner and market-value guidance' },
      { type: 'security',   text: 'Usernames are now validated when accounts are created or updated: email addresses and spaces are rejected, and usernames must be at least three characters' },
      { type: 'fix',        text: 'Vehicle registration lookups now accept VDGL successful responses that include non-blocking warnings, avoiding incorrect lookup failures when usable vehicle data is returned' },
      { type: 'fix',        text: 'Portal feed documents with no accessible file are now safely excluded, and outbound communications consistently include the required legal footer' },
    ],
  },
  {
    version: '2.0.05',
    date: 'Monday 1 September 2026',
    label: 'Client-Specific Scheduled Weekly Fleet Movements Reporting',
    updatedBy: 'Matthew Murphy',
    current: false,
    changes: [
      { type: 'feature',    text: 'Admin Insights now includes Scheduled Reports — client-specific weekly ons/offs email schedules with recipient selection from portal users plus optional CC addresses' },
      { type: 'feature',    text: 'Weekly fleet movements report covers the full previous UK calendar week (Mon 00:01 to Sun 23:59) with separate Added, Removed and Temp sections plus a combined chronological view' },
      { type: 'feature',    text: 'Schedules support preview last week, test send to the admin inbox, run now, enable/pause, Excel-compatible CSV attachment, and skip-empty-week behaviour' },
      { type: 'feature',    text: 'Background scheduler sends due weekly reports automatically on the configured UK day/time with run history and last-run status tracking' },
      { type: 'fix',        text: 'Permanent additions now populate correctly for instant-cover fleets; spreadsheet Cover Start uses the added date and Cover End uses the client policy end date' },
      { type: 'fix',        text: 'Scheduled Reports recipient picker layout corrected so names, emails and roles remain visible against global form CSS' },
    ],
  },
  {
    version: '2.0.04',
    date: 'Thursday 28 August 2026',
    label: 'Policy Period Archiving, Historical Reporting & Controlled Renewal',
    updatedBy: 'Matthew Murphy',
    current: false,
    changes: [
      { type: 'feature',    text: 'Policy periods now preserve a numbered current period and immutable archived snapshots, protecting historic fleet evidence when client policy dates are renewed' },
      { type: 'feature',    text: 'SuperAdmin renewal controls added to Client Details with UK policy dates, review and final confirmation; Client and Fleet Manager views remain focused on the current live policy period' },
      { type: 'feature',    text: 'Admin users can view archived policy-period snapshots and select an archived Fleet Status evidence view without changing live operational data' },
      { type: 'feature',    text: 'Fleet Status now shows total known gross vehicle weight and the number of vehicles awaiting a retained UKVD weight' },
      { type: 'compliance', text: 'Production policy-period backfill created current Period 1 records and assigned eligible fleet vehicles for ABC Fresh Produce, Delifresh and XSPAN; clients without valid policy dates were not changed' },
      { type: 'security',   text: 'Controlled release safeguards added: named local and Azure SQL rollback checkpoints, isolated staging API/frontend/database resources, staging-only mail redirect, and MFA validation before live promotion' },
    ],
  },
  {
    version: '2.0.03',
    date: 'Thursday 13 August 2026',
    label: 'UKVD Vehicle Classification, Vehicle History Notes & Addition Email Site Details',
    updatedBy: 'Matthew Murphy',
    current: false,
    changes: [
      { type: 'fix',     text: 'UKVD VehicleClass is now the authoritative vehicle-type source — classifications display in a friendly form with the UKVD value, for example Truck (HCV) and Van (LCV), instead of incorrectly defaulting commercial vehicles to Car' },
      { type: 'fix',     text: 'Vehicle weight classification now prioritises UKVD Gross Combined Weight; cars show their actual returned weight, for example Car (1,715 kg), while LCVs retain commercial bands such as Up to 3.5T and heavier vehicles use the applicable band through Up to 40T' },
      { type: 'feature', text: 'Vehicle History now supports custom audit notes — authorised users can add notes directly from the history modal, with a red timeline entry showing the note author and timestamp' },
      { type: 'feature', text: 'Vehicle addition emails now include the selected Site/Location for permanent and temporary additions across submission, instant cover, approval and rejection notifications' },
      { type: 'feature', text: 'Insights reporting retains the exact UKVD gross vehicle weight returned during vehicle searches and exposes it in Fleet Status, Vehicle Changes and applicable custom report exports' },
    ],
  },
  {
    version: '2.0.02',
    date: 'Thursday 19 June 2026',
    label: 'Fleet Manager Enhancements, Silent Remove, User Site Management & Performance',
    current: false,
    changes: [
      { type: 'feature',    text: 'Fleet Manager user screens — Edit and Send Invitation Email actions added; fleet managers can now update user details and send portal invitations to users within their own client' },
      { type: 'feature',    text: 'Change Site quick-action added to all user rows — fleet managers and admins can move a user between sites without opening the full edit form' },
      { type: 'feature',    text: 'New fleet-manager-scoped endpoints: PUT /Users/fleet-manager/{id} (update user) and PUT /Users/{id}/change-site (site reassignment), both scoped to the caller\'s own client' },
      { type: 'feature',    text: 'Invitation emails extended — fleet managers, admins and super users can now send portal invitations; previously restricted to SuperAdmin only' },
      { type: 'feature',    text: 'SuperAdmin silent vehicle removal — \ud83d\udd07 button added to all vehicle rows for SuperAdmin users; immediately deactivates the vehicle with no client emails sent' },
      { type: 'perf',       text: 'Dashboard load time halved for admin users — all 6 stat API calls now execute in a single parallel batch instead of two sequential rounds, eliminating one full network round-trip' },
      { type: 'perf',       text: 'Dashboard load time improved for client users — getStats, getCount, getUnreadCount and getPendingReferrals now run in one parallel batch' },
      { type: 'perf',       text: 'Sites page load time reduced — removed unnecessary Client entity Include from all SitesController query paths, eliminating a redundant database JOIN on every sites fetch' },
      { type: 'fix',        text: 'Change of Site vehicle search now strips spaces before matching so partial searches like \"WBP\" correctly match registrations stored as \"YJ23 WBP\"' },
      { type: 'fix',        text: 'Change of Site search placeholder updated to \"Type any part of the reg, e.g. last 3 letters\" to clarify partial search capability' },
      { type: 'fix',        text: 'Non-business owner/keeper vehicle submission emails now include a director identification prompt requesting the specific director who owns the vehicle, not just \"Owner/Individual\"' },
    ],
  },
  {
    version: '2.0.01',
    date: 'Monday 15 June 2026',
    label: 'Workstream Visibility, Instant Cover Persistence & Deployment Reliability',
    current: false,
    changes: [
      { type: 'fix',        text: 'Instant Cover persistence fixed — client cover settings now save reliably, including when optional insurer email fields are blank' },
      { type: 'fix',        text: 'Client details save fixed — optional insurer, midterm and Myshield contact email fields now send blank values as null to avoid backend email validation failures' },
      { type: 'fix',        text: 'Pending cover visibility fixed — vehicle status now consistently uses Cover Pending so new submissions correctly appear in the admin workstream' },
      { type: 'feature',    text: 'Workstream pending cover rows now show referral reason flags including Modifications, Individual Owner/Keeper and High Value' },
      { type: 'feature',    text: 'Workstream Pending Cover and All tabs now include expandable Pend Detail rows showing submitted-by, modification details, owner/keeper details, high-value threshold or standard review status' },
      { type: 'fix',        text: 'Vehicle modification submissions now require modification details before the form can be submitted, with matching backend validation' },
      { type: 'fix',        text: 'Pending referred named drivers now filter by selected client, preventing another client’s pending drivers appearing on the wrong client driver page' },
      { type: 'feature',    text: 'Pending named drivers admin view now includes company visibility when reviewing all clients' },
      { type: 'fix',        text: 'Midterm email fallback restored in production Azure settings so workstream submission, approval and rejection emails are copied to the midterm team when no client-specific midterm email is set' },
      { type: 'feature',    text: 'My Feed: admin users now see a pulsing Workstream summary bar, colour-coded green, amber or red based on pending workstream volume' },
      { type: 'feature',    text: 'My Feed: admin fleet shortcut renamed from My Fleet to All Fleets while client users continue to see My Fleet' },
      { type: 'fix',        text: 'Deployment script improved — resource creation is now idempotent, UK South is used for resource-group checks, backend deploy uses az webapp deploy, and Static Web App deployment explicitly targets production' },
      { type: 'compliance', text: 'Production database patched for legacy vehicle status mismatch so affected pending-cover vehicle records appear correctly in the workstream' },
    ],
  },
  {
    version: '1.91',
    date: 'Tuesday 3 June 2026',
    label: 'VDGL Live VRM Lookup, Mandatory Training Gate & Vehicle List Enhancements',
    current: false,
    changes: [
      { type: 'feature',    text: 'VRM registration lookup switched from mock to Vehicle Data Global Ltd (VDGL) PayGo Tier 1 live API — returns make, model, year, colour, body style and weight class automatically' },
      { type: 'fix',        text: 'VRM lookup frontend API call corrected to POST /Vehicles/lookup (was calling a non-existent GET endpoint, causing all lookups to silently fail)' },
      { type: 'security',   text: 'Mandatory Academy training gate — all non-SuperAdmin users must complete academy training before accessing any portal features; gate is non-dismissable and auto-unlocks on completion' },
      { type: 'feature',    text: 'Academy completion synced from backend on login — users who completed training on another device are not blocked' },
      { type: 'feature',    text: 'Vehicle list: Policy Start column shows vehicle inception date with GMT confirmation time; Policy End shows client policy end date with (23:59); Days Cover Remaining / Type of Cover column shows days to policy end with Perm/Temp badge' },
      { type: 'feature',    text: 'Vehicle list: cover status filter, site filter, and Reg A–Z sort toggle added (pure client-side, no loading impact)' },
      { type: 'feature',    text: 'Temp vehicles: Policy Start/End and Days Remaining now show the temp cover period (max 30 days) not the full client policy period' },
      { type: 'feature',    text: 'TWIMC PDF: temporary vehicle banner added (amber, non-dismissable) showing cover period and 30-day max note; Requested By and Confirmed By added to details block; timestamp corrected to GMT' },
      { type: 'fix',        text: 'TWIMC PDF: FCA authorisation text removed from header (remains in footer); footer centred' },
      { type: 'fix',        text: 'Temp vehicle instant cover: now sends approved email + correct TWIMC (temp cover dates) instead of awaiting review email' },
      { type: 'feature',    text: 'Email recipient restructuring — all vehicle action emails send 3 separate copies: client admin, midterm inbox, and the submitting user (each with personalised greeting and TWIMC PDF where applicable)' },
      { type: 'fix',        text: 'All email footers updated to © Myshield — A member of the Yourshield Family' },
      { type: 'feature',    text: 'Duplicate vehicle registration check on save — admin sees which fleet holds the reg; client sees a generic message' },
      { type: 'fix',        text: 'Motor Claims document description updated to clear form completion wording; live DB patched via startup migration' },
      { type: 'fix',        text: 'Feed document delete fixed — authorization changed to consistent IsAdmin() check; PUT endpoint added to documents' },
    ],
  },
  {
    version: '1.9',
    date: 'Monday 2 June 2026',
    label: 'Driver Referral Redesign & Email/PDF Improvements',
    current: false,
    changes: [
      { type: 'feature',    text: 'Driver form redesign — email and phone marked as optional, penalty points and licence number/expiry fields removed, replaced with licence image upload and new disclosure questions' },
      { type: 'feature',    text: 'New driver disclosure questions: notifiable medical conditions and unspent non-motoring convictions, required on all referral submissions' },
      { type: 'feature',    text: 'Licence image upload now available directly in both add and edit driver modes' },
      { type: 'feature',    text: 'Welcome wizard: "Yourshield Myshield" renamed to "Myshield" throughout; Bulk Upload step removed from client-facing tour' },
      { type: 'feature',    text: 'Footer: Terms of Business link added (yourshield.co.uk/terms-of-business/)' },
      { type: 'fix',        text: 'Vehicle save error now includes the specific reason, e.g. registration already present on another fleet' },
      { type: 'fix',        text: 'All email timestamps updated from UTC to GMT' },
      { type: 'fix',        text: 'Approval emails now show "Requested By" above "Processed By" in the decision summary block' },
      { type: 'compliance', text: 'Email footer updated with contact instruction for unrecognised vehicle changes' },
      { type: 'fix',        text: 'PDF cover letter footer centred; FCA authorisation text removed from header (retained in footer only)' },
      { type: 'fix',        text: 'PDF period of cover now shows approved time in GMT (current time if today, 00:00 GMT if future-dated)' },
      { type: 'feature',    text: 'Backend: MTA value threshold configurable per-client; driver declaration fields (notifiable conditions, unspent convictions) added to Driver model' },
    ],
  },
  {
    version: '1.8',
    date: 'Monday 1 June 2026',
    label: 'Referred Drivers & Workstream Enhancements',
    current: false,
    changes: [
      { type: 'feature',    text: 'Referred Named Drivers — admin workstream tab to approve or reject referred driver submissions, with full name, client, points and penalty code visibility' },
      { type: 'feature',    text: 'Client dashboard — full-width Pending Referred Named Drivers panel below stat tiles, matching the Recent Activity layout' },
      { type: 'feature',    text: 'Vehicle removal requests — admin approval modal now pre-populates the cover-end date from the client\'s requested removal date/time, with a "Client requested removal" info note' },
      { type: 'feature',    text: 'Removals workstream table — new Requested Cover End column showing the client\'s requested date and time' },
      { type: 'fix',        text: 'InfoTooltip — widened to match vehicle form width, ✕ dismiss button added to top-right corner, repositioned to avoid bottom-of-screen cutoff' },
      { type: 'fix',        text: 'To Whom It May Concern letter — cover period now shows current time when start date is today, and 00:00 GMT for future-dated cover' },
      { type: 'compliance', text: 'All timestamps changed from UTC to GMT across the portal, PDFs and email notifications' },
      { type: 'fix',        text: 'Date formats standardised to DD/MM/YYYY across all views, modals and exports' },
      { type: 'fix',        text: 'Wave emoji removed from the Named Driver referrals popup' },
      { type: 'fix',        text: 'Navigation menu item renamed from "Named Drivers" to "Referred Named Drivers"' },
    ],
  },
  {
    version: '1.7',
    date: 'Friday 29 May 2026',
    label: 'Bug Fixes & Performance',
    current: false,
    changes: [
      { type: 'fix',        text: 'Admin Insights: Vehicle Changes and Fleet Status reports now require a client or date-range filter — prevents database timeout when "All Clients" and "All Time" are both selected' },
      { type: 'perf',       text: 'Backend: All Insights Controller queries converted from Include() entity hydration to Select() projections — single efficient JOIN queries, dramatically reduced query time' },
      { type: 'fix',        text: 'Admin Tools: User Activity Report — Activity Timeline and Per-User Summary now each have their own independent export-to-Excel download button' },
      { type: 'fix',        text: 'Vehicle management: Add Permanent Vehicle modal width aligned to match the Add Temporary Vehicle modal (800 px max-width)' },
      { type: 'compliance', text: 'Version Control Log added to Security & Compliance — full release history from v1.0 visible to admin users' },
      { type: 'fix',        text: 'Excel exports: Sheet name sanitisation added — removes illegal characters and truncates to the 31-character Excel limit in Client and Admin Insights' },
    ],
  },
  {
    version: '1.6',
    date: 'Tuesday 19 May 2026',
    label: 'Insights & Reporting',
    current: false,
    changes: [
      { type: 'feature',    text: 'Admin Insights — Vehicle Changes report with full per-vehicle change history, date filtering and Excel export' },
      { type: 'feature',    text: 'Admin Insights — Fleet Status report: Full Policy Transactions, Live on Cover, and Removed / Off Cover sub-tabs' },
      { type: 'feature',    text: 'Admin Insights — Custom Report builder across five categories: Pending Cover, On Cover, Temp Additions, Removal Requests, and Change of Vehicle' },
      { type: 'feature',    text: 'Client Insights — live cover fleet, removed / off-cover, and full policy vehicle transactions exported to Excel' },
      { type: 'compliance', text: 'All Insights reports include client and date-range filtering to ensure outputs are scoped and auditable' },
      { type: 'fix',        text: 'Cover notice Excel exports corrected for removed and off-cover vehicles — previously only the live cover export was functional' },
    ],
  },
  {
    version: '1.5',
    date: 'Wednesday 6 May 2026',
    label: 'My Feed & Communications',
    current: false,
    changes: [
      { type: 'feature',    text: 'My Feed activity stream — personalised notification feed for workstream updates, approvals and portal events' },
      { type: 'feature',    text: 'Cover Notices — automated cover confirmation and rejection notices accessible by both client and admin users' },
      { type: 'feature',    text: 'Feed Management — admin ability to manage feed items, pin announcements and configure feed content' },
      { type: 'fix',        text: 'Portal-wide unread-badge count now correctly resets on navigation to My Feed' },
      { type: 'security',   text: 'Feed content access restricted by role — client users see only their own notice history; admin users see all' },
    ],
  },
  {
    version: '1.4',
    date: 'Monday 27 April 2026',
    label: 'Academy & Training',
    current: false,
    changes: [
      { type: 'feature',    text: 'Myshield Academy module — structured training content for fleet management and compliance topics' },
      { type: 'compliance', text: 'Training Overview tracker added to Security & Compliance — senior management can monitor staff training completion' },
      { type: 'feature',    text: 'DVLA penalty code reference library integrated into named driver management' },
      { type: 'compliance', text: 'Academy completion status included in SYSC 3 attestation supporting evidence' },
      { type: 'fix',        text: 'Academy progress indicators now correctly reflect per-module completion state on return visits' },
    ],
  },
  {
    version: '1.3',
    date: 'Wednesday 15 April 2026',
    label: 'Named Drivers & Licence Management',
    current: false,
    changes: [
      { type: 'feature',    text: 'Named Driver module — add, edit and manage named drivers per client fleet' },
      { type: 'compliance', text: 'Driving licence number capture gated behind a GDPR consent modal with full consent audit logging per driver' },
      { type: 'security',   text: 'Licence images (front and back) stored securely with access restricted to authorised personnel only' },
      { type: 'feature',    text: 'Licence expiry tracking with visual warnings for drivers expiring within 60 days' },
      { type: 'compliance', text: 'Outstanding penalty points and DVLA penalty code recording added to support insurer disclosure obligations' },
    ],
  },
  {
    version: '1.2',
    date: 'Monday 30 March 2026',
    label: 'Compliance Suite',
    current: false,
    changes: [
      { type: 'compliance', text: 'GDPR consent modal — mandatory 2-step declaration on first login covering Privacy & Data Notice and role-specific Usage Policy' },
      { type: 'compliance', text: 'Consent Records log — full audit trail of all GDPR and licence data consent events with timestamp and user' },
      { type: 'compliance', text: 'SYSC 3 Attestation — senior management systems and controls sign-off with PDF download and digital record' },
      { type: 'compliance', text: 'TCF MI Report — quarterly Treating Customers Fairly evidence pack generated automatically from workstream data' },
      { type: 'security',   text: 'MTA Register — mid-term adjustment notification register with FCA reference logging and export' },
      { type: 'fix',        text: 'Consent version reference updated across all declaration pages to reflect current policy version' },
    ],
  },
  {
    version: '1.1',
    date: 'Monday 16 March 2026',
    label: 'Workstream & Approval Engine',
    current: false,
    changes: [
      { type: 'feature',    text: 'Workstream management — full MTA workflow: pending cover approvals, temp additions, removal requests, and change-of-vehicle requests' },
      { type: 'feature',    text: 'Vehicle approval workflow — admin approve / reject with cover effective date, notes and automated cover notice generation' },
      { type: 'compliance', text: 'Cover status lifecycle enforced: Pending Cover → Active (On Cover) → Inactive (Off Cover) — no manual status bypass permitted' },
      { type: 'feature',    text: 'Bulk vehicle upload via CSV with full undo capability' },
      { type: 'security',   text: 'Full audit change log — every vehicle status change recorded immutably with user ID, date and time' },
      { type: 'fix',        text: 'Approval notes now correctly saved against the vehicle record on both approve and reject actions' },
    ],
  },
  {
    version: '1.0',
    date: 'Thursday 5 March 2026',
    label: 'Initial Platform Launch',
    current: false,
    changes: [
      { type: 'feature',    text: 'Core fleet management portal — clients, sites and vehicle management with role-based access control' },
      { type: 'feature',    text: 'Admin portal: user management, client onboarding, site configuration and vehicle list' },
      { type: 'feature',    text: 'Client portal: individual fleet view with vehicle cover-status visibility' },
      { type: 'security',   text: 'Secure authentication — JWT session management, mandatory MFA for admin accounts, and account lockout after 5 failed attempts' },
      { type: 'compliance', text: 'System Health monitoring — API and database connectivity checks with response-time tracking for FCA operational resilience' },
      { type: 'feature',    text: 'Portal Wizard — guided onboarding flow for new admin users' },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────────
// Badge helpers
// ─────────────────────────────────────────────────────────────────────────────────

const TYPE_META: Record<ChangeType, { label: string; bg: string; color: string }> = {
  feature:    { label: 'Feature',    bg: '#dbeafe', color: '#1d4ed8' },
  fix:        { label: 'Fix',        bg: '#dcfce7', color: '#15803d' },
  perf:       { label: 'Perf',       bg: '#fef9c3', color: '#854d0e' },
  security:   { label: 'Security',   bg: '#ede9fe', color: '#6d28d9' },
  compliance: { label: 'Compliance', bg: '#fff1f2', color: '#be123c' },
  removed:    { label: 'Removed',    bg: '#fee2e2', color: '#b91c1c' },
};

const Badge: React.FC<{ type: ChangeType }> = ({ type }) => {
  const m = TYPE_META[type];
  return (
    <span className="cl-badge" style={{ background: m.bg, color: m.color }}>
      {m.label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────────

const ChangeLog: React.FC = () => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>(
    Object.fromEntries(CHANGELOG.map((e) => [e.version, e.current]))
  );

  const toggle = (v: string) =>
    setExpanded((prev) => ({ ...prev, [v]: !prev[v] }));

  return (
    <div className="cl-wrap">

      {/* ── Header card ── */}
      <div className="cl-header-card">
        <div>
          <h3 className="cl-header-heading">Version Control Log</h3>
          <p className="cl-header-sub">
            A complete history of Myshield platform releases. Each version represents a
            grouped deployment reviewed and approved prior to release.
            Yourshield Limited &nbsp;·&nbsp; FRN: 617848
          </p>
        </div>
        <div className="cl-version-tag">
          <span className="cl-version-tag-label">Current Release</span>
          <span className="cl-version-tag-value">v{PORTAL_VERSION}</span>
        </div>
      </div>

      {/* ── Log table card ── */}
      <div className="cl-log-card">

        {/* Column labels */}
        <div className="cl-col-header">
          <span>Version</span>
          <span>Release</span>
          <span>Deployment Date</span>
          <span />
        </div>

        {CHANGELOG.map((entry) => {
          const isOpen = expanded[entry.version] ?? false;
          return (
            <div
              key={entry.version}
              className={`cl-entry${entry.current ? ' cl-entry--current' : ''}`}
            >
              <button
                className="cl-entry-header"
                onClick={() => toggle(entry.version)}
                aria-expanded={isOpen}
              >
                <span className="cl-version-pill">v{entry.version}</span>

                <span className="cl-entry-label">
                  {entry.label}
                  {entry.current && (
                    <span className="cl-current-badge">Current</span>
                  )}
                  {entry.updatedBy && (
                    <span className="cl-entry-attribution">Updated by {entry.updatedBy}</span>
                  )}
                </span>

                <span className="cl-entry-date">
                  <span className="cl-entry-date-day">{entry.date.split(' ').slice(0, 1).join(' ')}</span>
                  <span className="cl-entry-date-full">{entry.date.split(' ').slice(1).join(' ')}</span>
                </span>

                <span className="cl-chevron">{isOpen ? '▲' : '▼'}</span>
              </button>

              {isOpen && (
                <ul className="cl-change-list">
                  {entry.changes.map((c, i) => (
                    <li key={i} className="cl-change-item">
                      <Badge type={c.type} />
                      <span className="cl-change-text">{c.text}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}

        <p className="cl-footer-note">
          Each version represents a grouped deployment of changes to the Myshield fleet
          management environment. All changes are subject to internal review and testing
          before release. Version numbers follow a major.minor scheme.
        </p>
      </div>

    </div>
  );
};

export default ChangeLog;
